import { logger } from '@/lib/logger';

import type { ChatRunContext, ChatRunHandlers } from './chatProgressPoll';

export interface Entry {
  key: string;
  orgId: string;
  conversationId: string;
  taskId: string;
  handlers: ChatRunHandlers;
  pollTimer: ReturnType<typeof setTimeout> | null;
  watchdogTimer: ReturnType<typeof setTimeout> | null;
  pollCount: number;
  consecutiveFailures: number;
  startedAt: number;
  resolved: boolean;
  suppressToast: boolean;
  // Whether a `paused` chain finalizes the run as complete. Proposals opt in
  // (a paused turn is "ready for review"); main-chat runs resume after a pause.
  treatPausedAsComplete: boolean;
}

export const POLL_INTERVAL_MS = 2000;
// Mirrors the reasoning-chat progress hook's derived wall-clock cap — above
// the interactive chain hard `time_limit` 780s (apps/reasoning/tasks.py:1505)
// and a last-resort ceiling relative to the server reapers (processing 900s /
// queued 3600s, apps/reasoning/tasks.py:2472-2473), which resolve a
// genuinely-stuck chain first.
export const MAX_WALL_MS = 20 * 60 * 1000;
export const MAX_TRACKERS = 25;

// Prefers the oldest non-visible run (off-screen, re-derives on remount); falls back to oldest overall.
export function pickEvictableTracker(
  runs: Map<string, Entry>,
  visibleKeys: Set<string>
): Entry | undefined {
  let nonVisible: Entry | undefined;
  let oldest: Entry | undefined;
  for (const entry of runs.values()) {
    if (!oldest || entry.startedAt < oldest.startedAt) {
      oldest = entry;
    }
    if (
      !visibleKeys.has(entry.key) &&
      (!nonVisible || entry.startedAt < nonVisible.startedAt)
    ) {
      nonVisible = entry;
    }
  }
  return nonVisible ?? oldest;
}

export function buildCtx(
  e: Entry,
  key: string,
  taskId: string
): ChatRunContext {
  return {
    key,
    orgId: e.orgId,
    conversationId: e.conversationId,
    taskId,
    pollCount: e.pollCount,
    suppressToast: e.suppressToast,
  };
}

// Runs a handler in a contained try-catch so a throw never aborts the tracker's own resolution path; handles both sync and async handlers.
export function callHandlerSafely(
  label: string,
  context: Record<string, unknown>,
  fn: (() => void | Promise<void>) | undefined
): void | Promise<void> {
  if (!fn) {
    return;
  }
  const onError = (err: unknown): void => {
    logger.error(
      `[chatRunTracker] ${label} handler threw`,
      err instanceof Error ? err : new Error(String(err)),
      { component: 'reasoning', ...context }
    );
  };
  try {
    const result = fn();
    if (result instanceof Promise) {
      return result.catch(onError);
    }
  } catch (err) {
    onError(err);
  }
}
